import React, { useState, useEffect, useCallback } from 'react';
import { api, Ticket, Comment, User, AuditLog } from './api';
import './App.css';

const LOGS_KEY = 'zendesk_login_logs';
const AUTH_KEY = 'zendesk_auth';
const AUTH_EXPIRY_DAYS = 30;

interface LoginLog {
  type: 'login' | 'logout';
  at: string;
}

function loadLogs(): LoginLog[] {
  try {
    const s = localStorage.getItem(LOGS_KEY);
    return s ? JSON.parse(s) : [];
  } catch {
    return [];
  }
}

function saveLog(log: LoginLog) {
  const logs = loadLogs();
  logs.unshift(log);
  if (logs.length > 50) logs.pop();
  localStorage.setItem(LOGS_KEY, JSON.stringify(logs));
}

function getStoredAuth(): boolean {
  try {
    const s = localStorage.getItem(AUTH_KEY);
    if (!s) return false;
    const { expiresAt } = JSON.parse(s);
    return typeof expiresAt === 'number' && expiresAt > Date.now();
  } catch {
    return false;
  }
}

function saveAuth() {
  const expiresAt = Date.now() + AUTH_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
  localStorage.setItem(AUTH_KEY, JSON.stringify({ expiresAt }));
}

function clearAuth() {
  localStorage.removeItem(AUTH_KEY);
}

function App() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [byFilter, setByFilter] = useState<Record<string, Ticket[]>>({});
  const [filterIds, setFilterIds] = useState<string[]>([]);
  const [recap, setRecap] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<number | null>(null);
  const [ticketDetail, setTicketDetail] = useState<{ ticket: Ticket; comments: Comment[]; users: User[] } | null>(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [loginLogs, setLoginLogs] = useState<LoginLog[]>([]);
  const [nextLogins, setNextLogins] = useState<{ date: string; start: string; end: string }[]>([]);
  const [cronState, setCronState] = useState<{ active: boolean; schedule: string; description: string; lastCheck: string; inWorkingHours: boolean } | null>(null);
  const [working, setWorking] = useState(false);
  const [loggedIn, setLoggedIn] = useState(() => getStoredAuth());
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [ticketRecaps, setTicketRecaps] = useState<Record<number, string>>({});
  const [loadingRecap, setLoadingRecap] = useState<number | null>(null);
  const [replyNote, setReplyNote] = useState('');
  const [zendeskLogs, setZendeskLogs] = useState<AuditLog[] | null>(null);
  const [zendeskLogsLoading, setZendeskLogsLoading] = useState(false);
  const [zendeskLogsError, setZendeskLogsError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'open'>('all');

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getTickets();
      setTickets(data.tickets);
      setByFilter(data.byFilter || {});
      setFilterIds(data.filterIds || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRecap = async () => {
    try {
      const data = await api.getRecap();
      setRecap(data.recap);
    } catch (err) {
      setRecap('Error: ' + (err instanceof Error ? err.message : 'Failed to load recap'));
    }
  };

  const loadStatus = useCallback(async () => {
    try {
      const s = await api.getStatus();
      setNextLogins(s.nextLogins || []);
      setWorking(s.working);
      setCronState(s.cronState || null);
    } catch {
      setNextLogins([]);
      setCronState(null);
    }
  }, []);

  const handleLogout = () => {
    clearAuth();
    saveLog({ type: 'logout', at: new Date().toISOString() });
    setLoginLogs(loadLogs());
    setLoggedIn(false);
    setSelectedTicket(null);
    setTicketDetail(null);
    setReply('');
    setConfirmSend(false);
  };

  const handleLogin = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setLoginError(null);
    if (!loginPassword.trim()) {
      setLoginError('Enter password');
      return;
    }
    setLoginLoading(true);
    try {
      await api.login(loginPassword.trim());
      saveAuth();
      saveLog({ type: 'login', at: new Date().toISOString() });
      setLoginLogs(loadLogs());
      setLoggedIn(true);
      setLoginPassword('');
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : 'Invalid password');
    } finally {
      setLoginLoading(false);
    }
  };

  useEffect(() => {
    setLoginLogs(loadLogs());
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (loggedIn) {
      saveLog({ type: 'login', at: new Date().toISOString() });
      setLoginLogs(loadLogs());
    }
  }, [loggedIn]);

  useEffect(() => {
    if (loggedIn) loadTickets();
  }, [loggedIn, loadTickets]);

  const loadZendeskLogs = async () => {
    setZendeskLogsLoading(true);
    setZendeskLogsError(null);
    try {
      const { audit_logs } = await api.getAuditLogs('login');
      setZendeskLogs(audit_logs);
    } catch (err) {
      setZendeskLogsError(err instanceof Error ? err.message : 'Failed to load Zendesk logs');
      setZendeskLogs(null);
    } finally {
      setZendeskLogsLoading(false);
    }
  };

  const loadTicketRecap = async (ticketId: number) => {
    if (ticketRecaps[ticketId]) return;
    setLoadingRecap(ticketId);
    try {
      const { recap } = await api.getTicketRecap(String(ticketId));
      setTicketRecaps((prev) => ({ ...prev, [ticketId]: recap }));
    } catch {
      setTicketRecaps((prev) => ({ ...prev, [ticketId]: 'Error loading recap' }));
    } finally {
      setLoadingRecap(null);
    }
  };

  const openTicket = async (ticketId: number) => {
    setSelectedTicket(ticketId);
    setTicketDetail(null);
    setReply('');
    setConfirmSend(false);
    try {
      const data = await api.getTicket(String(ticketId), true);
      setTicketDetail(data);
      if (data.recap) setTicketRecaps((prev) => ({ ...prev, [ticketId]: data.recap! }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load ticket');
    }
  };

  const closeTicket = () => {
    setSelectedTicket(null);
    setTicketDetail(null);
    setReply('');
    setConfirmSend(false);
  };

  const TicketCard = ({
    ticket,
    formatDate,
    openTicket,
    ticketRecaps,
    loadTicketRecap,
    loadingRecap,
    selectedTicket,
  }: {
    ticket: Ticket;
    formatDate: (s: string) => string;
    openTicket: (id: number) => void;
    ticketRecaps: Record<number, string>;
    loadTicketRecap: (id: number) => void;
    loadingRecap: number | null;
    selectedTicket: number | null;
  }) => (
    <div className={`ticket-card ${selectedTicket === ticket.id ? 'selected' : ''}`}>
      <div className="ticket-header">
        <span className="ticket-id">#{ticket.id}</span>
        <span className={`status status-${ticket.status}`}>{ticket.status}</span>
      </div>
      <h3>{ticket.subject}</h3>
      <p className="ticket-meta">
        Updated {formatDate(ticket.updated_at)} • {ticket.priority || 'normal'} priority
      </p>
      {ticket.description && (
        <p className="ticket-desc">{ticket.description.substring(0, 120)}...</p>
      )}
      <div className="ticket-recap">
        {ticketRecaps[ticket.id] ? (
          <p className="recap-text">{ticketRecaps[ticket.id]}</p>
        ) : (
          <button
            className="btn-recap"
            onClick={(e) => { e.stopPropagation(); loadTicketRecap(ticket.id); }}
            disabled={loadingRecap === ticket.id}
          >
            {loadingRecap === ticket.id ? '...' : 'AI Recap'}
          </button>
        )}
      </div>
      <button className="btn-generate" onClick={() => openTicket(ticket.id)}>
        Open ticket
      </button>
    </div>
  );

  const handleGenerateReply = async () => {
    if (!selectedTicket) return;
    setGenerating(true);
    setReply('');
    try {
      const { reply: generated } = await api.generateReply(String(selectedTicket), replyNote);
      setReply(generated);
    } catch (err) {
      setReply('Error: ' + (err instanceof Error ? err.message : 'Failed to generate'));
    } finally {
      setGenerating(false);
    }
  };

  const handleSendReply = async () => {
    if (!selectedTicket || !reply.trim()) return;
    setSending(true);
    try {
      await api.sendReply(String(selectedTicket), reply.trim());
      setReply('');
      setConfirmSend(false);
      closeTicket();
      loadTickets();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (s: string) => new Date(s).toLocaleString();
  const getUserName = (id: number) => ticketDetail?.users?.find((u) => u.id === id)?.name || `User ${id}`;

  const filteredTickets = statusFilter === 'open' ? tickets.filter((t) => t.status === 'open') : tickets;
  const filteredByFilter: Record<string, Ticket[]> = {};
  filterIds.forEach((fid) => {
    const list = (byFilter[fid] || []).filter((t) => statusFilter === 'all' || t.status === 'open');
    if (list.length > 0) filteredByFilter[fid] = list;
  });

  const filterLabels: Record<string, string> = {
    '360043522240': 'Filter 1',
    '7648740360850': 'Filter 2',
    search: 'Search results',
  };

  if (!loggedIn) {
    return (
      <div className="app">
        <header className="header">
          <h1>Zendesk Ticket Assistant</h1>
          <p className="subtitle">PrestaShop Help</p>
        </header>
        <main className="main login-screen">
          <form className="login-box" onSubmit={handleLogin}>
            <h2>Welcome</h2>
            <p>Sign in to access the ticket dashboard</p>
            <input
              type="password"
              className="login-password"
              placeholder="Password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              autoFocus
              disabled={loginLoading}
            />
            {loginError && <p className="login-error">{loginError}</p>}
            <button type="submit" className="btn-login" disabled={loginLoading}>
              {loginLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="header">
        <div className="header-row">
          <div>
            <h1>Zendesk Ticket Assistant</h1>
            <p className="subtitle">PrestaShop Help • AI-powered support</p>
          </div>
          <button className="btn-logout" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      <main className="main">
        <section className="dashboard-row">
          <div className="panel login-logs">
            <h3>Login / Logout logs (app)</h3>
            <div className="logs-list">
              {loginLogs.length === 0 ? (
                <p className="muted">No logs yet</p>
              ) : (
                loginLogs.slice(0, 10).map((log, i) => (
                  <div key={i} className={`log-item log-${log.type}`}>
                    <span className="log-type">{log.type}</span>
                    <span className="log-time">{formatDate(log.at)}</span>
                  </div>
                ))
              )}
            </div>
          </div>
          <div className="panel zendesk-logs">
            <h3>Zendesk connexion logs</h3>
            <button
              className="btn-load-logs"
              onClick={loadZendeskLogs}
              disabled={zendeskLogsLoading}
            >
              {zendeskLogsLoading ? 'Chargement...' : 'Charger les logs Zendesk'}
            </button>
            {zendeskLogsError && <p className="zendesk-logs-error">{zendeskLogsError}</p>}
            {zendeskLogs && (
              <div className="zendesk-logs-list">
                {zendeskLogs.length === 0 ? (
                  <p className="muted">Aucun log de connexion</p>
                ) : (
                  zendeskLogs.slice(0, 20).map((log) => (
                    <div key={log.id} className="zendesk-log-item">
                      <span className="zl-actor">{log.actor_name}</span>
                      <span className="zl-time">{formatDate(log.created_at)}</span>
                      {log.ip_address && <span className="zl-ip">{log.ip_address}</span>}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          <div className="panel next-logins">
            <h3>Next working hours (Tunisia)</h3>
            <div className="next-logins-list">
              {nextLogins.map((n, i) => (
                <div key={i} className="next-login-item">
                  <span className="nl-date">{n.date}</span>
                  <span className="nl-time">9:00 – 18:00</span>
                </div>
              ))}
            </div>
            {working && <span className="badge-online">Currently online</span>}
          </div>
        </section>

        {cronState && (
          <section className="panel cron-state">
            <h3>Cron state</h3>
            <div className="cron-details">
              <span className={cronState.active ? 'badge-active' : 'badge-inactive'}>
                {cronState.active ? 'Active' : 'Inactive'}
              </span>
              <span className="cron-schedule">{cronState.schedule}</span>
              <p className="cron-desc">{cronState.description}</p>
              <p className="cron-meta">Last check: {new Date(cronState.lastCheck).toLocaleString()} • In working hours: {cronState.inWorkingHours ? 'Yes' : 'No'}</p>
            </div>
          </section>
        )}

        <section className="actions">
          <button onClick={loadTickets} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh tickets'}
          </button>
          <button onClick={loadRecap} disabled={loading}>
            Get AI recap & suggestions
          </button>
        </section>

        {error && <div className="error">{error}</div>}

        {recap && (
          <section className="recap-card">
            <h2>AI Recap</h2>
            <div className="recap-content">{recap}</div>
          </section>
        )}

        <section className="tickets-layout">
          <div className="tickets-section tickets-left">
            <div className="tickets-header-row">
              <h2>Tickets ({filteredTickets.length})</h2>
              <select
                className="status-filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'all' | 'open')}
              >
                <option value="all">Tous (new, open, pending, hold)</option>
                <option value="open">Open uniquement</option>
              </select>
            </div>
            {loading ? (
              <p>Loading...</p>
            ) : filteredTickets.length === 0 ? (
              <p>No tickets found.</p>
            ) : (
              <div className="tickets-by-filter">
                {filterIds.map((fid) => {
                  const list = filteredByFilter[fid] || [];
                  if (list.length === 0) return null;
                  return (
                    <div key={fid} className="filter-group">
                      <h4>{filterLabels[fid] || fid} ({list.length})</h4>
                      <div className="tickets-list">
                        {list.map((t) => (
                          <TicketCard
                            key={t.id}
                            ticket={t}
                            formatDate={formatDate}
                            openTicket={openTicket}
                            ticketRecaps={ticketRecaps}
                            loadTicketRecap={loadTicketRecap}
                            loadingRecap={loadingRecap}
                            selectedTicket={selectedTicket}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className={`ticket-detail-panel tickets-right ${selectedTicket ? 'visible' : 'empty'}`}>
            {selectedTicket ? (
              <>
                <div className="panel-header">
                  <h2>Ticket #{selectedTicket}</h2>
                  <button className="btn-close" onClick={closeTicket}>×</button>
                </div>

                {ticketDetail && (
                  <div className="ticket-detail-content">
                    <div className="ticket-recap-detail">
                      {ticketRecaps[selectedTicket] && (
                        <>
                          <h3>AI Recap</h3>
                          <p className="recap-text">{ticketRecaps[selectedTicket]}</p>
                        </>
                      )}
                    </div>
                    <div className="exchanges-block">
                      <h3>Exchanges</h3>
                      <div className="exchanges-list">
                        {ticketDetail.comments.map((c) => (
                          <div key={c.id} className={`exchange-item ${c.public ? 'public' : 'internal'}`}>
                            <div className="exchange-meta">
                              {getUserName(c.author_id)} • {formatDate(c.created_at)}
                              {c.public ? ' (public)' : ' (internal)'}
                            </div>
                            <div className="exchange-body">{c.body}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="ai-reply-section">
                      <h3>AI-generated reply</h3>
                      <div className="reply-note-field">
                        <label>Note / instruction pour l'IA (optionnel)</label>
                        <textarea
                          value={replyNote}
                          onChange={(e) => setReplyNote(e.target.value)}
                          placeholder="Ex: Mentionner que nous travaillons sur une correction..."
                          rows={2}
                        />
                      </div>
                      <button
                        className="btn-generate"
                        onClick={handleGenerateReply}
                        disabled={generating}
                      >
                        {generating ? 'Generating...' : 'Generate AI reply'}
                      </button>
                      {reply && (
                        <>
                          <textarea
                            value={reply}
                            onChange={(e) => setReply(e.target.value)}
                            placeholder="Hello... Cordially"
                            rows={8}
                          />
                          <div className="reply-actions">
                            <button
                              onClick={() => setConfirmSend(true)}
                              disabled={sending || !reply.trim()}
                            >
                              Send reply
                            </button>
                            <button onClick={() => setReply('')}>Clear</button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="ticket-detail-placeholder">
                <p>Cliquez sur un ticket pour afficher les détails</p>
              </div>
            )}
          </div>
        </section>

        {confirmSend && (
          <div className="modal-overlay" onClick={() => setConfirmSend(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h3>Confirm send</h3>
              <p>Send this reply to the customer?</p>
              <div className="modal-reply-preview">{reply}</div>
              <div className="modal-actions">
                <button className="btn-confirm" onClick={handleSendReply} disabled={sending}>
                  {sending ? 'Sending...' : 'Yes, send'}
                </button>
                <button onClick={() => setConfirmSend(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
